import React, { useState, useEffect, useCallback } from "react";
import { 
  Search, 
  Calculator, 
  Repeat, 
  TrendingUp, 
  ArrowUpDown,
  Zap,
  Clock,
  Shield,
  Info,
  Star,
  Activity
} from "lucide-react";

interface PriceData {
  symbol: string;
  price: number;
  confidence: number;
  timestamp: number;
  source?: string;
}

interface PriceCalculatorProps {
  symbols: string[];
  onFetchPrice: (symbol: string) => Promise<PriceData | null>;
  onFetchCrossPrice: (fromSymbol: string, toSymbol: string) => Promise<PriceData | null>;
  compact?: boolean;
}

interface SwapCalculation {
  fromAmount: number;
  toAmount: number;
  rate: number;
  impact: number;
  route?: string[];
}

export const PriceCalculator: React.FC<PriceCalculatorProps> = ({ 
  symbols = [], 
  onFetchPrice, 
  onFetchCrossPrice,
  compact = false 
}) => {
  const [fromSymbol, setFromSymbol] = useState("ALPH");
  const [toSymbol, setToSymbol] = useState("USDT");
  const [fromAmount, setFromAmount] = useState("1");
  const [singleSymbol, setSingleSymbol] = useState("");
  const [singlePrice, setSinglePrice] = useState<PriceData | null>(null);
  const [crossPrice, setCrossPrice] = useState<PriceData | null>(null);
  const [swapCalculation, setSwapCalculation] = useState<SwapCalculation | null>(null);
  const [loading, setLoading] = useState(false);

  const availableSymbols = Array.isArray(symbols) ? symbols : [];

  const handleSinglePriceLookup = async () => {
    if (!singleSymbol) return;
    setLoading(true);
    try {
      const price = await onFetchPrice(singleSymbol);
      setSinglePrice(price);
    } finally {
      setLoading(false);
    }
  };

  const handleCrossPriceLookup = useCallback(async () => {
    if (!fromSymbol || !toSymbol || !fromAmount) return;
    setLoading(true);
    try {
      const price = await onFetchCrossPrice(fromSymbol, toSymbol);
      setCrossPrice(price);
      
      if (price && parseFloat(fromAmount) > 0) {
        const amount = parseFloat(fromAmount);
        const toAmount = amount * price.price;
        
        setSwapCalculation({
          fromAmount: amount,
          toAmount,
          rate: price.price,
          impact: 0,
          route: [fromSymbol, toSymbol]
        });
      }
    } finally {
      setLoading(false);
    }
  }, [fromSymbol, toSymbol, fromAmount, onFetchCrossPrice]);

  useEffect(() => {
    if (fromSymbol && toSymbol && fromAmount && parseFloat(fromAmount) > 0) {
      handleCrossPriceLookup();
    }
  }, [fromSymbol, toSymbol, fromAmount, handleCrossPriceLookup]);

  const swapTokens = () => {
    const tempSymbol = fromSymbol;
    setFromSymbol(toSymbol);
    setToSymbol(tempSymbol);
    
    if (swapCalculation) {
      setFromAmount(swapCalculation.toAmount.toString());
    }
  };

  const formatNumber = (num: number, decimals: number = 6): string => {
    if (num === 0) return "0";
    if (num < 0.001) return num.toFixed(8);  // Show 8 decimal places for very small numbers
    if (num < 1) return num.toFixed(decimals);
    if (num < 1000) return num.toFixed(4);
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  return compact ? (
    // Compact mode for sidebar
    <div className="space-y-4">
      {/* Compact Cross Price Calculator */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <select
            value={fromSymbol}
            onChange={(e) => setFromSymbol(e.target.value)}
            className="w-full p-2 bg-black/40 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 transition-all"
            title="Select from token"
          >
            {availableSymbols.map((symbol) => (
              <option key={symbol} value={symbol} className="bg-gray-900">{symbol}</option>
            ))}
          </select>
          <select
            value={toSymbol}
            onChange={(e) => setToSymbol(e.target.value)}
            className="w-full p-2 bg-black/40 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 transition-all"
            title="Select to token"
          >
            {availableSymbols.map((symbol) => (
              <option key={symbol} value={symbol} className="bg-gray-900">{symbol}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={fromAmount}
            onChange={(e) => setFromAmount(e.target.value)}
            placeholder="Amount"
            className="flex-1 p-2 bg-black/40 border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 transition-all"
          />
          <button
            onClick={swapTokens}
            className="p-2 bg-gray-700/50 hover:bg-gray-600/50 border border-gray-600 rounded-lg transition-all"
            title="Swap tokens"
          >
            <Repeat className="w-4 h-4 text-gray-300" />
          </button>
        </div>
        
        {swapCalculation && crossPrice && (
          <div className="p-3 bg-black/30 rounded-lg border border-gray-700/30">
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-400">
                {formatNumber(swapCalculation.toAmount)} {toSymbol}
              </div>
              <div className="text-gray-400 text-xs mt-1">
                Rate: 1 {fromSymbol} = {formatNumber(crossPrice.price)} {toSymbol}
              </div>
              <div className="text-gray-500 text-xs">
                {(crossPrice.confidence * 100).toFixed(1)}% confidence
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    // Full mode for main content
    <div className="space-y-8">
      {/* Enhanced Single Price Lookup */}
      <div className="group relative overflow-hidden bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-3xl border border-gray-800 hover:border-gray-700 transition-all duration-500 shadow-2xl hover:shadow-purple-500/10">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full blur-3xl transform translate-x-48 -translate-y-48"></div>
        
        <div className="relative p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/50 to-blue-500/50 rounded-2xl blur-lg opacity-75 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative p-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl group-hover:scale-105 transition-transform duration-300">
                  <Search className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent mb-1">
                  Token Price Lookup
                </h3>
                <p className="text-gray-400">Get real-time price data for any token</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-xl">
              <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
              <span className="text-purple-300 text-sm font-semibold">LIVE</span>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <select
                  value={singleSymbol}
                  onChange={(e) => setSingleSymbol(e.target.value)}
                  className="w-full p-4 bg-black/40 border border-gray-700 rounded-2xl text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 backdrop-blur-sm appearance-none cursor-pointer"
                  title="Select token to lookup"
                >
                  <option value="" className="bg-gray-900 text-gray-300">Select a token...</option>
                  {availableSymbols.map((symbol) => (
                    <option key={symbol} value={symbol} className="bg-gray-900 text-white">{symbol}</option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ArrowUpDown className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <button
                onClick={handleSinglePriceLookup}
                disabled={!singleSymbol || loading}
                className="group relative px-8 py-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold rounded-2xl hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-purple-500/25"
                title="Lookup token price"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
                <div className="relative flex items-center gap-2">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Zap className="w-5 h-5" />
                  )}
                  {loading ? 'Loading...' : 'Lookup'}
                </div>
              </button>
            </div>

            {singlePrice && (
              <div className="relative overflow-hidden bg-gradient-to-br from-black/60 to-gray-900/60 backdrop-blur-sm rounded-2xl border border-gray-800 p-6 animate-fade-in hover:border-purple-500/30 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full blur-2xl"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-2xl"></div>
                <div className="relative flex items-center gap-4">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                    <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-105 transition-transform duration-300">
                      {singlePrice.symbol.charAt(0)}
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                      <Star className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                      {singlePrice.symbol}
                      <span className="text-xs px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-300">
                        LIVE
                      </span>
                    </h4>
                    <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent mb-2 hover:scale-105 transition-transform duration-300 cursor-default">
                      ${formatNumber(singlePrice.price)} USD
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-emerald-400" />
                        <span className="text-gray-300">Confidence: </span>
                        <span className="text-emerald-400 font-semibold">{(singlePrice.confidence * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-400" />
                        <span className="text-gray-300">Updated: </span>
                        <span className="text-blue-400 font-semibold">Now</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Swap Calculator */}
      <div className="group relative overflow-hidden bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-xl rounded-3xl border border-gray-800 hover:border-gray-700 transition-all duration-500 shadow-2xl hover:shadow-emerald-500/10">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-full blur-3xl transform -translate-x-48 -translate-y-48"></div>
        
        <div className="relative p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl blur opacity-75"></div>
                <div className="relative p-4 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-300 to-blue-300 bg-clip-text text-transparent mb-1">
                  Swap Calculator
                </h3>
                <p className="text-gray-400">Calculate token swaps with real-time pricing</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-xl">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-emerald-300 text-sm font-semibold">ACTIVE</span>
            </div>
          </div>

          <div className="space-y-6">
            {/* From Token Section */}
            <div className="relative overflow-hidden bg-gradient-to-br from-black/60 to-gray-900/60 backdrop-blur-sm rounded-2xl border border-gray-800 p-6">
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-500/20 to-transparent rounded-full blur-xl"></div>
              <div className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-gray-300 font-medium">You Pay</label>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Activity className="w-3 h-3" />
                    <span>Balance: --</span>
                  </div>
                </div>
                <div className="flex gap-4">
                  <input
                    type="number"
                    value={fromAmount}
                    onChange={(e) => setFromAmount(e.target.value)}
                    placeholder="0.0"
                    className="flex-1 p-4 bg-black/40 border border-gray-700 rounded-2xl text-white placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all duration-300 backdrop-blur-sm text-xl font-semibold hover:border-gray-600 focus:bg-black/60"
                    title="Enter amount to pay"
                  />
                  <div className="relative">
                    <select
                      value={fromSymbol}
                      onChange={(e) => setFromSymbol(e.target.value)}
                      className="w-32 p-4 bg-black/40 border border-gray-700 rounded-2xl text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all duration-300 backdrop-blur-sm appearance-none cursor-pointer hover:border-gray-600 focus:bg-black/60"
                      title="Select token to pay with"
                    >
                      {availableSymbols.map((symbol) => (
                        <option key={symbol} value={symbol} className="bg-gray-900 text-white">{symbol}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ArrowUpDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={swapTokens}
                className="group relative p-4 bg-gradient-to-br from-gray-800 to-black border border-gray-700 rounded-2xl hover:border-gray-600 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-emerald-500/20"
                title="Swap token positions"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Repeat className="relative w-6 h-6 text-gray-400 group-hover:text-emerald-400 transition-colors transform group-hover:rotate-180 duration-500" />
              </button>
            </div>

            {/* To Token Section */}
            <div className="relative overflow-hidden bg-gradient-to-br from-black/60 to-gray-900/60 backdrop-blur-sm rounded-2xl border border-gray-800 p-6">
              <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-500/20 to-transparent rounded-full blur-xl"></div>
              <div className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-gray-300 font-medium">You Receive</label>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Info className="w-3 h-3" />
                    <span>Estimated</span>
                  </div>
                </div>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={swapCalculation ? formatNumber(swapCalculation.toAmount) : "0.0"}
                    readOnly
                    className="flex-1 p-4 bg-black/40 border border-gray-700 rounded-2xl text-white backdrop-blur-sm text-xl font-semibold cursor-not-allowed opacity-75 hover:opacity-90 transition-opacity"
                    title="Amount you will receive"
                  />
                  <div className="relative">
                    <select
                      value={toSymbol}
                      onChange={(e) => setToSymbol(e.target.value)}
                      className="w-32 p-4 bg-black/40 border border-gray-700 rounded-2xl text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 backdrop-blur-sm appearance-none cursor-pointer hover:border-gray-600 focus:bg-black/60"
                      title="Select token to receive"
                    >
                      {availableSymbols.map((symbol) => (
                        <option key={symbol} value={symbol} className="bg-gray-900 text-white">{symbol}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ArrowUpDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Swap Details */}
            {swapCalculation && crossPrice && (
              <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl border border-emerald-500/30 p-6 animate-fade-in hover:border-emerald-400/40 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/20 to-blue-500/20 rounded-full blur-2xl"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-2xl"></div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="relative group">
                      <div className="absolute inset-0 bg-emerald-500/30 rounded-xl blur-lg group-hover:bg-emerald-400/40 transition-all duration-300"></div>
                      <div className="relative p-3 bg-gradient-to-br from-emerald-500/30 to-blue-500/30 rounded-xl backdrop-blur-sm border border-emerald-500/20 group-hover:scale-105 transition-transform duration-300">
                        <TrendingUp className="w-5 h-5 text-emerald-400" />
                      </div>
                    </div>
                    <div>
                      <span className="text-lg font-semibold bg-gradient-to-r from-emerald-300 to-blue-300 bg-clip-text text-transparent">
                        Swap Analysis
                      </span>
                      <div className="text-xs text-gray-400 mt-1">Real-time market data</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="group relative overflow-hidden bg-black/30 rounded-xl p-4 border border-gray-800 hover:border-emerald-500/30 transition-all duration-300 hover:bg-black/40">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-full blur-xl"></div>
                      <div className="relative">
                        <div className="text-sm text-gray-400 mb-2 font-medium">Exchange Rate</div>
                        <div className="text-white font-bold text-lg group-hover:text-emerald-300 transition-colors">
                          1 {fromSymbol} = {formatNumber(swapCalculation.rate)} {toSymbol}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Live rate</div>
                      </div>
                    </div>
                    
                    <div className="group relative overflow-hidden bg-black/30 rounded-xl p-4 border border-gray-800 hover:border-blue-500/30 transition-all duration-300 hover:bg-black/40">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full blur-xl"></div>
                      <div className="relative">
                        <div className="text-sm text-gray-400 mb-2 font-medium">Price Confidence</div>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="text-emerald-400 font-bold text-lg group-hover:text-emerald-300 transition-colors">
                            {(crossPrice.confidence * 100).toFixed(1)}%
                          </div>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-3 h-3 transition-all duration-200 ${
                                  i < Math.floor(crossPrice.confidence * 5) 
                                    ? 'text-emerald-400 fill-current' 
                                    : 'text-gray-600'
                                }`} 
                              />
                            ))}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">Data quality</div>
                      </div>
                    </div>
                    
                    <div className="group relative overflow-hidden bg-black/30 rounded-xl p-4 border border-gray-800 hover:border-purple-500/30 transition-all duration-300 hover:bg-black/40">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-xl"></div>
                      <div className="relative">
                        <div className="text-sm text-gray-400 mb-2 font-medium">Network Fee</div>
                        <div className="text-blue-400 font-bold text-lg group-hover:text-blue-300 transition-colors">
                          ~0.001 ALPH
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Est. gas cost</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ); // Close full mode
};
